import React, { useState } from "react";
import { PanelContainer } from "@/components/chat/panels/PanelContainer";
import { FlashcardsPanelContent } from "@/components/flashcards/FlashcardsPanelContent";
import { PromptBox } from "@/components/content-chat/PromptBox";
import { GeneratingCard } from "@/components/content-chat/GeneratingCard";
import { FlashcardSettingsModal, FlashcardSettings } from "@/components/content-chat/modals";
import { useContentContext } from "@/components/content-chat/ContentContext";
import { useContentTopicsFromChapters } from "@/hooks/useContentChapters";
import { useAuth } from "@/hooks/useAuth";
import { useFlashcardsStore } from "@/features/flashcards/store";
import { generateFlashcardsWithAI } from "@/features/flashcards/api";
import { getContentSourceText } from "@/lib/content/getContentSourceText";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import type { ContentType } from "@/types/content";

interface ContentFlashcardsPanelProps {
  isMobile?: boolean;
  contentId?: string | null;
  chatId?: string | null;
}

export const ContentFlashcardsPanel: React.FC<ContentFlashcardsPanelProps> = (props) => {
  const { user } = useAuth();
  const { content } = useContentContext();
  const { loadDecks } = useFlashcardsStore();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { topics: availableTopics } = useContentTopicsFromChapters(
    content?.id,
    content?.type as ContentType | undefined
  );

  // Get the effective contentId from props or context
  const effectiveContentId = props.contentId ?? content?.id ?? null;

  const flashcardsIcon = (
    <svg
      viewBox="0 0 256 256"
      className="h-4 w-4 text-primary"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="currentColor"
        d="M180,72H36A20,20,0,0,0,16,92V204a20,20,0,0,0,20,20H180a20,20,0,0,0,20-20V92A20,20,0,0,0,180,72Zm-4,128H40V96H176ZM240,52V176a12,12,0,0,1-24,0V56H64a12,12,0,0,1,0-24H220A20,20,0,0,1,240,52Z"
      />
    </svg>
  );

  const handleGenerate = async () => {
    if (!user || !content) return;

    setIsGenerating(true);
    try {
      // Fetch source text from content
      const sourceText = await getContentSourceText(content.id, content.type as ContentType);
      
      if (!sourceText) {
        toast({
          title: "Content not ready",
          description: "Please wait for the content to be processed before generating flashcards.",
          variant: "destructive",
        });
        return;
      }

      // Generate flashcards with AI
      const result = await generateFlashcardsWithAI({
        userId: user.id,
        contentId: content.id,
        contentType: content.type,
        topic: content.title,
        sourceText,
        numCards: 10,
      });

      // Refresh the store to show the new deck
      await loadDecks();
      
      // Also invalidate React Query cache for consistency
      queryClient.invalidateQueries({ queryKey: ["flashcard-decks", user.id] });
      
      toast({
        title: "Flashcards generated!",
        description: `Created ${result.cards.length} flashcards in "${result.deck.title}"`,
      });
    } catch (error: any) {
      console.error("Error generating flashcards:", error);
      
      // Check for usage limit error
      if (error?.message?.includes("USAGE_LIMIT_EXCEEDED") || error?.error === "USAGE_LIMIT_EXCEEDED") {
        toast({
          title: "Daily limit reached",
          description: "You've reached your daily flashcard generation limit. Upgrade for unlimited access.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Generation failed",
          description: error.message || "Failed to generate flashcards. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSettings = () => {
    setSettingsOpen(true);
  };

  const handleSettingsSubmit = async (settings: FlashcardSettings) => {
    if (!user || !content) return;
    
    console.log("Flashcard settings:", settings);
    setIsGenerating(true);
    try {
      // Fetch source text from content
      const sourceText = await getContentSourceText(content.id, content.type as ContentType);
      
      if (!sourceText) {
        toast({
          title: "Content not ready",
          description: "Please wait for the content to be processed before generating flashcards.",
          variant: "destructive",
        });
        return;
      }

      // Generate flashcards with custom settings
      const result = await generateFlashcardsWithAI({
        userId: user.id,
        contentId: content.id,
        contentType: content.type,
        topic: settings.topics?.[0] || content.title,
        sourceText,
        numCards: settings.count || 10,
      });

      // Refresh the store to show the new deck
      await loadDecks();
      
      // Also invalidate React Query cache for consistency
      queryClient.invalidateQueries({ queryKey: ["flashcard-decks", user.id] });
      
      toast({
        title: "Flashcards generated!",
        description: `Created ${result.cards.length} flashcards in "${result.deck.title}"`,
      });
    } catch (error: any) {
      console.error("Error generating flashcards:", error);
      
      if (error?.message?.includes("USAGE_LIMIT_EXCEEDED") || error?.error === "USAGE_LIMIT_EXCEEDED") {
        toast({
          title: "Daily limit reached",
          description: "You've reached your daily flashcard generation limit. Upgrade for unlimited access.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Generation failed",
          description: error.message || "Failed to generate flashcards. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (!user) {
    return (
      <PanelContainer
        {...props}
        title="Flashcards"
        description={
          <>
            Sign in to save and study flashcards. Ask Shattara AI:{" "}
            <span className="font-bold">"Create 10 flashcards about photosynthesis."</span>
          </>
        }
        icon={flashcardsIcon}
      >
        <div className="p-3 text-xs text-muted-foreground">
          Please log in to use flashcards.
        </div>
      </PanelContainer>
    );
  }

  return (
    <>
      <PanelContainer
        {...props}
        title="Flashcards"
        description={
          <>
            Try asking Shattara AI:{" "}
            <span className="font-bold">
              "Create 10 flashcards about photosynthesis."
            </span>
          </>
        }
        icon={flashcardsIcon}
      >
        <div className="flex flex-col">
          {/* Always show PromptBox or GeneratingCard */}
          {isGenerating ? (
            <GeneratingCard type="flashcard" />
          ) : (
            <PromptBox
              type="flashcard"
              onGenerate={handleGenerate}
              onSettings={handleSettings}
              isGenerating={isGenerating}
            />
          )}
          {/* Show deck content - FlashcardsPanelContent handles loading and empty states */}
          <div className="mt-4">
            <FlashcardsPanelContent
              userId={user.id}
              contentId={effectiveContentId}
              chatId={props.chatId ?? null}
            />
          </div>
        </div>
      </PanelContainer>
      
      <FlashcardSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSubmit={handleSettingsSubmit}
        availableTopics={availableTopics}
      />
    </>
  );
};
